import { EditOutlined, EyeOutlined, FileExcelOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, Space, Spin, Tag, Typography } from "antd";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  OB_EXCEL_REPORT,
  PACKING_REPORT_TEMPLATE_EXPORT,
  PACKING_REPORT_TEMPLATE_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import ListTable from "common/ListTable";
import {
  PACKING_REPORT_TEMPLATE_EXPORT_PERMISSION,
  PACKING_REPORT_TEMPLATE_STORE_PERMISSION,
  PACKING_REPORT_TEMPLATE_UPDATE_PERMISSION,
  PACKING_REPORT_TEMPLATE_VIEW_PERMISSION,
} from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import { isMobileView } from "../../Common/AppHelper";
import CustomFilter from "../../Common/CustomFilter";
import Header from "../../Common/Header";
import HandleNotification from "common/Notification";

const PackingReportTemplate = (props) => {
  // Props
  const { width } = props;

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [list, setList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rangeData, setRangeData] = useState();
  const [selectedIds, setSelectedIds] = useState(null);
  const [searchValues, setSearchValues] = useState();
  const [exportLoading, setExportLoading] = useState(false);

  // Antd
  const { Title } = Typography;

  // Others
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Active":
        return "green";
      case "Inactive":
        return "red";
      case "Pending":
        return "orange";
      case "Draft":
        return "blue";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Buyer Name",
      dataIndex: ["buyer", "name"],
      key: "buyer_name",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office", "name"],
      key: "buying_office_name",
      width: "25%",
    },
    {
      title: "Sales Contract No",
      dataIndex: ["sales_contract", "reference_no"],
      key: "sales_contract_no",
    },
    {
      title: "Origin",
      dataIndex: "origin",
      key: "origin",
    },
    {
      title: "Status",
      dataIndex: "status_label",
      key: "status_label",
      render: (status) => (
        <Tag color={getStatusBadgeColor(status)} key={status}>
          {status}
        </Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: "10%",
      align: "right",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getList = async (passedObject) => {
    const filter = passedObject;
    setError(null);
    let res = await getData(PACKING_REPORT_TEMPLATE_LIST, false, filter);

    if (res) {
      let masterData = res?.data;
      const templateList = masterData?.data?.data;
      setList(templateList);
      setTotalData(masterData?.data?.total);
      setRangeData(masterData?.data?.per_page);
    } else {
      setError("Could not load this list.");
    }
    setLoading(false);
  };

  useEffect(() => {
    const filterValues = localStorage.getItem("import_pi_filter_value")
      ? JSON.parse(localStorage.getItem("import_pi_filter_value"))
      : null;
    setLoading(true);

    getList({
      page: currentPage,
      ...(filterValues && { ...filterValues }),
    });
  }, [currentPage, searchValues]);

  // const removePi = async (value) => {
  //   let res = await deleteData(`/api/accounting/import-pi/delete/${value}`);
  //   window.location.reload();
  // };

  const onDownloadExcel = async (id, name) => {
    setExportLoading(true);
    const query = `${PACKING_REPORT_TEMPLATE_EXPORT}/${id}`;

    const response = await getData(
      query,
      false,
      {},
      { "Content-Type": "blob" }, //Custome Header
      "arraybuffer", //Response-Type to get excel file
    );

    if (response && response?.status === 200) {
      const outputFilename = `packing_report_template_${name}.xlsx`;

      // If you want to download file automatically using link attribute.
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
    } else {
      HandleNotification("error", "bottomRight", "Error getting excel report!", null);
    }
    setExportLoading(false);
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          key: "0",
          disabled: !hasPermission([PACKING_REPORT_TEMPLATE_UPDATE_PERMISSION]),
          label: (
            <div style={{ display: "block", width: "100%" }}>
              <EditOutlined /> Edit
            </div>
          ),
          onClick: () => {
            if (hasPermission([PACKING_REPORT_TEMPLATE_UPDATE_PERMISSION])) {
              navigate(`/ex-factory/packing-report-template/edit/${data.id}`);
            }
          },
        },
        // {
        //   key: "1",
        //   disabled: !hasPermission([PACKING_REPORT_TEMPLATE_VIEW_PERMISSION]),
        //   label: (
        //     <div style={{ display: "block", width: "100%" }}>
        //       <EyeOutlined /> View
        //     </div>
        //   ),
        //   onClick: () => {
        //     if (hasPermission([PACKING_REPORT_TEMPLATE_VIEW_PERMISSION])) {
        //       navigate(`/ex-factory/packing-report-template/view/${data.id}`);
        //     }
        //   },
        // },
        {
          key: "2",
          disabled: !hasPermission([PACKING_REPORT_TEMPLATE_EXPORT_PERMISSION]),
          label: (
            <div style={{ display: "block", width: "100%" }}>
              {exportLoading ? (
                <span>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  Loading...
                </span>
              ) : (
                <>
                  <FileExcelOutlined style={{ marginRight: 8 }} /> Export
                </>
              )}
            </div>
          ),
          onClick: () => {
            if (hasPermission([PACKING_REPORT_TEMPLATE_EXPORT_PERMISSION])) {
              onDownloadExcel(data.id, data.name);
            }
          },
        },
      ]}
    />
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const extra = (
    <Space>
      {/* <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button> */}
      <Button key="3" size="small" type="primary" disabled={!hasPermission([PACKING_REPORT_TEMPLATE_STORE_PERMISSION])}>
        <Link to="/ex-factory/packing-report-template/add">
          {hasPermission([PACKING_REPORT_TEMPLATE_STORE_PERMISSION]) ? "Add New" : "No Permission"}
          {/* Add New */}
        </Link>
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
    () => <CustomFilter showFilter={showFilter} tableData={list} fetchTableData={getList} filterName="import_pi" />,
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header title={<Title level={5}>Packing Report Template</Title>} subTitle="" actions={extra} />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {/* {memoizedCustomFilter} */}

          <ListTable
            loading={loading}
            error={error}
            onRetry={() =>
              getList({
                page: currentPage,
              })
            }
            emptyText="No records to show yet."
            dataSource={list}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default PackingReportTemplate;
